import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Info } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Card, Spinner } from "@/components/earnos/kit";
import { colors } from "@/lib/theme";

const SOURCE = {
  verified: { label: "Verified", color: colors.success },
  self_reported: { label: "Self-reported", color: colors.warning },
  ai_inferred: { label: "AI-inferred", color: colors.info },
  not_available: { label: "Not available", color: colors.onSurfaceSecondary },
};

export default function Reputation() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      const [rep, ins] = await Promise.all([apiFetch("/reputation"), apiFetch("/insights")]);
      setData(rep);
      setInsights(ins);
    } catch (e) { setLoadError(e.message || "Service unavailable. Please retry."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner className="py-24" />;

  if (loadError && !loading) return <LoadError title="Reputation" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} data-testid="rep-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Reputation</h1>
      </div>

      <p className="text-base text-ink-2 leading-relaxed">EARNOS never reduces you to one arbitrary score. Each signal is labeled by how it was confirmed.</p>

      <div className="rounded-xl border border-brand-light bg-surface-2 p-5 mt-5">
        <h3 className="text-lg font-extrabold text-ink mb-2">What EARNOS understands about your income</h3>
        {insights && !insights.evidence_sufficient ? (
          <p className="text-base text-ink-2 leading-relaxed">{insights.message}</p>
        ) : insights ? (
          <>
            {insights.strongest_pattern ? (
              <div className="mt-3">
                <p className="earnos-kicker">Strongest income pattern</p>
                <p className="text-xl font-extrabold text-ink mt-0.5">{insights.strongest_pattern.source}</p>
                <p className="text-base text-ink-2 mt-0.5">${insights.strongest_pattern.verified_revenue} across {insights.strongest_pattern.payments} logged payments · <span className="text-ok font-bold">SELF-REPORTED</span></p>
              </div>
            ) : null}
            {insights.whats_working?.length ? (
              <div className="mt-3"><p className="earnos-kicker">What&apos;s working</p>{insights.whats_working.map((w, i) => <p key={i} className="text-base text-ink-2 mt-1">• {w.label} — {w.detail}</p>)}</div>
            ) : null}
            {insights.whats_not_working?.length ? (
              <div className="mt-3"><p className="earnos-kicker">What&apos;s not working</p>{insights.whats_not_working.map((w, i) => <p key={i} className="text-base text-ink-2 mt-1">• {w.label} — {w.detail}</p>)}</div>
            ) : null}
            {insights.compound_this?.length ? (
              <div className="mt-3"><p className="earnos-kicker !text-ok">Compound this ↑</p>{insights.compound_this.map((c, i) => <p key={i} className="text-base text-ink-2 mt-1">• {c}</p>)}</div>
            ) : null}
            <p className="text-sm text-ink-3 mt-4 leading-relaxed">Evidence used: {insights.verified_payments} logged payments · recurring ${insights.recurring_revenue} · {insights.repeat_customers} repeat customers</p>
          </>
        ) : null}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        {(data?.dimensions || []).map((d, i) => {
          const s = SOURCE[d.source] || SOURCE.not_available;
          return (
            <Card key={i}>
              <div className="flex items-center justify-between">
                <p className="text-lg font-bold text-ink">{d.name}</p>
                <span className="px-2 py-0.5 rounded-full text-sm font-bold" style={{ color: s.color, backgroundColor: s.color + "22" }}>{s.label}</span>
              </div>
              <p className="text-2xl font-extrabold text-ink mt-2">{d.value === null ? "—" : d.value}</p>
              <p className="text-sm text-ink-2 mt-1 leading-relaxed">{d.detail}</p>
            </Card>
          );
        })}
      </div>

      {data?.note ? (
        <div className="flex items-start gap-1.5 mt-8">
          <Info size={14} className="text-ink-2 mt-0.5 shrink-0" />
          <p className="text-sm text-ink-2 leading-tight">{data.note}</p>
        </div>
      ) : null}
    </div>
  );
}
